import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import './ChatWidget.css';
import { Send, Clock, MessageSquare, Trash2 } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatWidgetProps {
  alertId?: number;
  insightId?: number;
  propertyId?: number;
  documentTitle: string;
  propertyName: string;
  onClose: () => void;
}

interface Conversation {
  id: number;
  thread_id: string;
  title: string;
  context_type: string;
  property_name?: string;
  last_message_at: string;
  message_count: number;
  created_at: string;
  alert_id?: number;
  insight_id?: number;
  property_id?: number;
}

// Helper function to clean OpenAI citation markers
const cleanCitations = (text: string): string => {
  if (!text) return '';
  
  // Remove OpenAI citation markers like 【5:15†source】
  let cleaned = text.replace(/【\d+:\d+†[^】]+】/g, '');
  
  // Remove markdown-style citations like [source](sandbox:/path)
  cleaned = cleaned.replace(/\[source\]\(sandbox:[^)]+\)/g, '');
  
  // Remove numbered citations like [1], [2]
  cleaned = cleaned.replace(/\[\d+\]/g, '');
  
  // Clean up excessive whitespace but preserve line breaks
  cleaned = cleaned.replace(/[^\S\n]+/g, ' '); // Replace horizontal whitespace with single space
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n'); // Max 2 consecutive newlines
  cleaned = cleaned.trim();
  
  return cleaned;
};

const ChatWidget: React.FC<ChatWidgetProps> = ({ alertId, insightId, propertyId, documentTitle, propertyName, onClose }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Generate a unique key for storing thread ID based on the context
  const threadStorageKey = alertId 
    ? `chat_thread_alert_${alertId}` 
    : `chat_thread_insight_${insightId}_${propertyId || 'all'}`;
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(() => {
    // Try to load existing thread ID from localStorage
    return localStorage.getItem(threadStorageKey);
  });
  const [error, setError] = useState<string | null>(null);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentStreamRef = useRef<string>('');

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Load conversation history
  useEffect(() => {
    loadConversations();
  }, []);
  
  const loadConversations = async () => {
    try {
      setLoadingHistory(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = (import.meta.env.VITE_API_URL || 'https://cityscrapeai-production.up.railway.app').replace(/\/+$/, '');
      
      const response = await fetch(`${API_URL}/api/conversations?limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to only show conversations relevant to current context
        const filtered = data.filter((conv: Conversation) => {
          if (alertId) return conv.alert_id === alertId;
          if (insightId) return conv.insight_id === insightId;
          return true;
        });
        setConversations(filtered);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const loadConversation = async (conv: Conversation) => {
    try {
      setLoadingHistory(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = (import.meta.env.VITE_API_URL || 'https://cityscrapeai-production.up.railway.app').replace(/\/+$/, '');
      
      const response = await fetch(`${API_URL}/api/conversations/${conv.thread_id}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Set thread ID and messages
        setThreadId(conv.thread_id);
        localStorage.setItem(threadStorageKey, conv.thread_id);
        
        const formattedMessages = data.messages.map((msg: any) => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content
        }));
        setMessages(formattedMessages);
        setShowHistory(false);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
      setError('Failed to load conversation');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const deleteConversation = async (conv: Conversation, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = (import.meta.env.VITE_API_URL || 'https://cityscrapeai-production.up.railway.app').replace(/\/+$/, '');
      
      const response = await fetch(`${API_URL}/api/conversations/${conv.thread_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Remove from list
        setConversations(prev => prev.filter(c => c.thread_id !== conv.thread_id));
        // Clear if it's the current thread
        if (threadId === conv.thread_id) {
          handleNewConversation();
        }
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  // Function to start a new conversation
  const handleNewConversation = () => {
    if (window.confirm('Start a new conversation?')) {
      // Clear thread ID from state and localStorage
      setThreadId(null);
      localStorage.removeItem(threadStorageKey);
      // Clear messages
      setMessages([]);
      setError(null);
      setShowHistory(false);
      console.log('🔄 Started new conversation');
      // Reload conversations to get latest
      loadConversations();
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);
    
    // Add user message to UI
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsStreaming(true);
    setStreamingContent('');
    currentStreamRef.current = '';

    try {
      const token = localStorage.getItem('auth_token'); // Fixed: use 'auth_token' not 'token'
      const API_URL = (import.meta.env.VITE_API_URL || 'https://cityscrapeai-production.up.railway.app').replace(/\/+$/, '');
      
      // Determine endpoint based on whether it's an alert or insight
      let chatUrl = '';
      if (insightId) {
        chatUrl = `${API_URL}/api/project-insights/${insightId}/chat${propertyId ? `?property_id=${propertyId}` : ''}`;
      } else if (alertId) {
        chatUrl = `${API_URL}/api/alerts/${alertId}/chat`;
      } else {
        throw new Error('Either alertId or insightId must be provided');
      }
      
      console.log('Chat API URL:', chatUrl);
      console.log('Auth token:', token ? `${token.substring(0, 10)}...` : 'No token');
      
      const response = await fetch(chatUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          message: userMessage,
          thread_id: threadId 
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to send message';
        try {
          const errorData = await response.json();
          console.error('API Error:', errorData);
          errorMessage = errorData.detail || errorMessage;
        } catch (jsonError) {
          // If response is not JSON (e.g., HTML error page), get text
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response stream');
      
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'thread_id') {
                console.log('🆔 Thread ID received:', data.thread_id);
                setThreadId(data.thread_id);
                // Persist thread ID to localStorage
                localStorage.setItem(threadStorageKey, data.thread_id);
              } else if (data.type === 'token') {
                currentStreamRef.current += data.content;
                
                // Clean citations from the accumulated content
                const cleanedContent = cleanCitations(currentStreamRef.current);
                
                // Update streaming content state
                setStreamingContent(cleanedContent);
                console.log('✍️ Streaming content updated, length:', cleanedContent.length);
                
                // Auto-scroll
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              } else if (data.type === 'done') {
                console.log('✅ Streaming complete');
                
                // Add final message with markdown formatting
                const finalContent = cleanCitations(currentStreamRef.current);
                setMessages(prev => [...prev, { role: 'assistant', content: finalContent }]);
                
                // Clear streaming state
                setStreamingContent('');
                setIsStreaming(false);
                
                // Reload conversations to update the history
                loadConversations();
              } else if (data.type === 'error') {
                console.log('❌ Error:', data.message);
                setError(data.message || 'An error occurred');
                setMessages(prev => prev.slice(0, -1)); // Remove empty assistant message
                setIsStreaming(false);
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError, 'Raw line:', line);
            }
          }
        }
      }
    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.message || 'Failed to send message. Please try again.');
      setMessages(prev => prev.slice(0, -1)); // Remove empty assistant message
      setIsStreaming(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    setInput(question);
  };

  return (
    <div className="chat-widget-container">
      <div className="chat-widget-box">
        <div className="chat-widget-header">
          <div>
            <div className="chat-widget-title">
              Ask AI{insightId ? ' about Insight' : ''}
              {threadId && (
                <span style={{ fontSize: '10px', opacity: 0.7, marginLeft: '4px' }}>
                  (Continuing)
                </span>
              )}
            </div>
            <div className="chat-widget-subtitle">
              {documentTitle} • {propertyName}
            </div>
          </div>
          <div className="chat-widget-header-buttons">
            {conversations.length > 0 && (
              <button 
                onClick={() => setShowHistory(!showHistory)}
                style={{
                  padding: '4px 12px',
                  fontSize: '13px',
                  background: showHistory ? 'rgba(255,255,255,0.2)' : 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  if (!showHistory) e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  if (!showHistory) e.currentTarget.style.background = 'transparent';
                }}
              >
                <Clock size={14} />
                History ({conversations.length})
              </button>
            )}
            {threadId && messages.length > 0 && (
              <button 
                className="chat-widget-new-conversation" 
                onClick={handleNewConversation}
                style={{
                  padding: '4px 12px',
                  fontSize: '13px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                New Chat
              </button>
            )}
            <button className="chat-widget-close" onClick={onClose} title="Close">×</button>
          </div>
        </div>

        <div className="chat-widget-body">
        <div className="chat-widget-messages">
          {/* Conversation History Panel */}
          {showHistory && (
            <div className="chat-widget-history-panel">
              <div className="chat-widget-history-title">
                <MessageSquare size={14} />
                Recent Conversations
              </div>
              {loadingHistory ? (
                <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>Loading...</div>
              ) : conversations.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {conversations.map(conv => (
                    <div
                      key={conv.thread_id}
                      onClick={() => loadConversation(conv)}
                      style={{
                        padding: '10px',
                        background: threadId === conv.thread_id ? '#e0e7ff' : 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (threadId !== conv.thread_id) {
                          e.currentTarget.style.background = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (threadId !== conv.thread_id) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#1f2937',
                        marginBottom: '4px',
                        paddingRight: '24px'
                      }}>
                        {conv.title}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        display: 'flex',
                        gap: '12px'
                      }}>
                        <span>{conv.message_count} messages</span>
                        <span>{
                          new Date(conv.last_message_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        }</span>
                      </div>
                      <button
                        onClick={(e) => deleteConversation(conv, e)}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          background: 'transparent',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.7,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '0.7';
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#6b7280', 
                  fontSize: '13px',
                  padding: '20px'
                }}>
                  No previous conversations
                </div>
              )}
            </div>
          )}
          
          {messages.length === 0 && (
            <div className="chat-widget-empty">
              <p>Ask questions about this agenda document and how it might affect your property.</p>
              <div className="chat-widget-suggestions">
                <button onClick={() => handleSuggestedQuestion("What are the key items in this agenda?")}>
                  What are the key items?
                </button>
                <button onClick={() => handleSuggestedQuestion("How might this impact my property?")}>
                  How might this impact my property?
                </button>
                <button onClick={() => handleSuggestedQuestion("Are there any zoning changes?")}>
                  Are there any zoning changes?
                </button>
              </div>
            </div>
          )}
          
          {messages.map((msg, i) => (
            <div key={i} className={`chat-widget-message chat-widget-message-${msg.role}`}>
              <div className="chat-widget-message-content">
                {msg.role === 'assistant' ? (
                  <ReactMarkdown
                        components={{
                          // Custom styling for markdown elements
                          p: ({node, ...props}) => <p style={{ marginBottom: '0.75em', lineHeight: '1.6' }} {...props} />,
                          ul: ({node, ...props}) => <ul style={{ marginLeft: '1.25em', marginBottom: '0.75em' }} {...props} />,
                          ol: ({node, ...props}) => <ol style={{ marginLeft: '1.25em', marginBottom: '0.75em' }} {...props} />,
                          li: ({node, ...props}) => <li style={{ marginBottom: '0.25em' }} {...props} />,
                          h1: ({node, children, ...props}) => <h1 style={{ fontSize: '1.5em', fontWeight: 'bold', marginBottom: '0.5em', marginTop: '0.5em' }} {...props}>{children}</h1>,
                          h2: ({node, children, ...props}) => <h2 style={{ fontSize: '1.3em', fontWeight: 'bold', marginBottom: '0.5em', marginTop: '0.5em' }} {...props}>{children}</h2>,
                          h3: ({node, children, ...props}) => <h3 style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '0.5em', marginTop: '0.5em' }} {...props}>{children}</h3>,
                          strong: ({node, ...props}) => <strong style={{ fontWeight: '600' }} {...props} />,
                          em: ({node, ...props}) => <em style={{ fontStyle: 'italic' }} {...props} />,
                          code: ({node, inline, ...props}: any) => 
                            inline ? (
                              <code style={{ 
                                backgroundColor: '#f3f4f6', 
                                padding: '0.125em 0.375em', 
                                borderRadius: '0.25em',
                                fontSize: '0.9em',
                                fontFamily: 'monospace'
                              }} {...props} />
                            ) : (
                              <code style={{ 
                                display: 'block',
                                backgroundColor: '#f3f4f6', 
                                padding: '0.75em', 
                                borderRadius: '0.5em',
                                fontSize: '0.9em',
                                fontFamily: 'monospace',
                                overflowX: 'auto',
                                marginBottom: '0.75em'
                              }} {...props} />
                            ),
                          blockquote: ({node, ...props}) => (
                            <blockquote style={{
                              borderLeft: '3px solid #BADA55',
                              paddingLeft: '1em',
                              marginLeft: '0',
                              marginBottom: '0.75em',
                              color: '#6b7280'
                            }} {...props} />
                          ),
                        }}
                      >
                        {cleanCitations(msg.content)}
                      </ReactMarkdown>
                ) : (
                  cleanCitations(msg.content)
                )}
              </div>
            </div>
          ))}
          
          {/* Streaming message container - always shows live streaming content */}
          {isStreaming && streamingContent && (
            <div className="chat-widget-message chat-widget-message-assistant">
              <div className="chat-widget-message-content">
                <div style={{ whiteSpace: 'pre-wrap' }}>{streamingContent}</div>
              </div>
            </div>
          )}
          
          {isStreaming && !streamingContent && (
            <div className="chat-widget-message chat-widget-message-assistant">
              <div className="chat-widget-message-content">
                <div className="chat-widget-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {error && (
          <div className="chat-widget-error">{error}</div>
        )}

        <div className="chat-widget-input-area">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about this document..."
            disabled={isStreaming}
          />
          <button
            onClick={sendMessage}
            disabled={isStreaming || !input.trim()}
          >
            <Send size={18} />
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default ChatWidget;

